import React, { useContext, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Select, { SelectOption } from "@amzn/meridian/select";
import AdminContext from "../../store/admin-context";
import AlertErrorWithMessage from "../alert/AlertErrorWithMessage";
import AdminApiFactory from "../../admin-api/AdminApiFactory";
import {
  BUSINESS_SPACE_OPTIONS,
  BUSINESS_SPACE_DROPDOWN_PLACEHOLDER,
  BUSINESS_SPACE_DISPLAY_LABELS,
  NODE_LIST_KEY,
  ALERT_NODE_LIST_LOAD_MESSAGE,
  parseBusinessSpace,
} from "../../constants";

export default function BusinessSpaceSelect({ width = 200, label = "" }) {
  const { selectedBusinessSpace, changeSelectedBusinessSpace, changeSelectedNode, loadNodeList } =
    useContext(AdminContext);
  const history = useHistory();
  const AdminApi = AdminApiFactory();
  const [showError, setShowError] = useState(false);

  // Guards against out-of-order responses: if the admin switches business space
  // again before an earlier getFacilities call resolves, only the latest
  // selection's response is allowed to update the node list.
  const latestBusinessSpaceRef = useRef<string>("");

  async function onChangeBusinessSpaceHandler(rawValue: string) {
    // Meridian Select can emit an empty value (placeholder); the facilities
    // endpoint requires a business space and would 400 on a blank one.
    const value = parseBusinessSpace(rawValue);
    if (!value) {
      return;
    }

    latestBusinessSpaceRef.current = value;
    changeSelectedBusinessSpace(value);
    // Clear any node selected under the previous business space so a stale node
    // cannot persist across the switch (and so the route below carries no node).
    changeSelectedNode("");
    // Clear any prior error up front so a stale banner from an earlier failed
    // selection cannot linger across a new selection (incl. when an earlier
    // request is superseded and its branch returns before reaching setShowError).
    setShowError(false);
    history.push(`/${value}`);

    try {
      const response = await AdminApi.getFacilities(value);
      if (latestBusinessSpaceRef.current !== value) {
        // A newer selection superseded this request; drop its result.
        return;
      }
      const nodes = response.data.nodes ?? [];
      loadNodeList(nodes);
      localStorage.setItem(NODE_LIST_KEY, JSON.stringify(nodes));
    } catch (ex) {
      if (latestBusinessSpaceRef.current !== value) {
        return;
      }
      console.error("Error loading nodes for business space", value, ex);
      loadNodeList([]);
      localStorage.removeItem(NODE_LIST_KEY);
      setShowError(true);
    }
  }

  return (
    <Column spacing="200">
      {showError && <AlertErrorWithMessage message={ALERT_NODE_LIST_LOAD_MESSAGE} />}
      <Select
        data-testid="businessSpaceSelect"
        value={selectedBusinessSpace}
        onChange={onChangeBusinessSpaceHandler}
        placeholder={BUSINESS_SPACE_DROPDOWN_PLACEHOLDER}
        width={width}
        label={label}
      >
        {BUSINESS_SPACE_OPTIONS.map((businessSpace) => (
          <SelectOption
            data-testid="businessSpaceSelectOption"
            key={businessSpace}
            value={businessSpace}
            label={BUSINESS_SPACE_DISPLAY_LABELS[businessSpace] || businessSpace}
          />
        ))}
      </Select>
    </Column>
  );
}
